/**
 * Whose browser storage is this. U0.4.
 *
 * Before multi-user, everything the client kept on the device was Doug's by
 * construction: one IndexedDB cache, a persisted queue of paused writes, a few
 * localStorage keys. A second person on the same browser would have painted the
 * first one's lists and replayed their queued writes under their own session.
 * So the cache is keyed by user id, and sign-out removes what belongs to the
 * user who signed out.
 *
 * Dependency-free on purpose (CLAUDE.md): `idb-keyval` is imported lazily and
 * both storages are parameters, so `test/user-storage.test.ts` can run it
 * against a fake pair under `node --test`.
 *
 * What this never touches: `sb-*` (the Supabase session, CLAUDE.md rule 7) and
 * `sl-theme` (a device preference, not a personal one).
 */

/** The pre-U0.4 key. Nobody reads it any more; startup deletes it once so a
 *  phone does not hold two seven-day caches. */
export const LEGACY_CACHE_KEY = 'schnauzelist-query-cache'

const CACHE_PREFIX = 'schnauzelist-query-cache:'

export const cacheKeyFor = (userId: string | null) =>
  userId ? `${CACHE_PREFIX}${userId}` : `${CACHE_PREFIX}anon`

export const scopedKey = (base: string, userId: string | null) => `${base}:${userId ?? 'anon'}`

/**
 * `scopedKey` for whoever is signed in on this device right now, by the stored
 * session (see `readStoredUserId`). What a screen's own remembered-choice key
 * goes through, so none of them has to know about `useAuth()` or the env.
 * Under `node --test` there is no env and no storage, so it answers `:anon`.
 */
export const userKey = (base: string) =>
  scopedKey(base, readStoredUserId((import.meta as { env?: Record<string, string | undefined> }).env?.VITE_SUPABASE_URL))

/** The one key that is per-list rather than per-user. It is a list's own order,
 *  and a list is one user's, but the next person on this device must not
 *  inherit it. */
const AISLE_ORDER_PREFIX = 'sl.aisleOrder.'

/** Never removed, whatever else matches. */
const isProtected = (key: string) => key.startsWith('sb-') || key === 'sl-theme'

export interface KeyStore {
  readonly length: number
  key(index: number): string | null
  removeItem(key: string): void
  getItem?(key: string): string | null
}

function sweep(store: KeyStore | undefined, remove: (key: string) => boolean) {
  if (!store) return
  try {
    // Collect first: removing while indexing shifts the keys under the loop.
    const doomed: string[] = []
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i)
      if (k && !isProtected(k) && remove(k)) doomed.push(k)
    }
    for (const k of doomed) store.removeItem(k)
  } catch { /* storage blocked: nothing there to clear */ }
}

function browserStore(name: 'localStorage' | 'sessionStorage'): KeyStore | undefined {
  try { return globalThis[name] } catch { return undefined }
}

/**
 * Removes one user's footprint: their IndexedDB cache, every localStorage and
 * sessionStorage key ending in `:${userId}`, and `sl.aisleOrder.*`. `null` is
 * the signed-out namespace (`:anon`).
 */
export async function clearUserStorage(
  userId: string | null,
  stores: { local?: KeyStore; session?: KeyStore } = {},
): Promise<void> {
  const suffix = `:${userId ?? 'anon'}`
  const mine = (k: string) => k.endsWith(suffix) || k.startsWith(AISLE_ORDER_PREFIX)
  sweep('local' in stores ? stores.local : browserStore('localStorage'), mine)
  sweep('session' in stores ? stores.session : browserStore('sessionStorage'), mine)
  if (typeof indexedDB === 'undefined') return
  try {
    const { del } = await import('idb-keyval')
    await del(cacheKeyFor(userId))
  } catch { /* a private window: there was no cache to remove */ }
}

/**
 * The signed-in user's id as Supabase left it in localStorage, read
 * synchronously. This is what lets the cache key be chosen at module load,
 * before `getSession()` resolves, so the shell's cold-load timing does not
 * wait on it. The same peek `App.tsx` makes for `hasStoredSession`.
 *
 * It is a guess that `onAuthStateChange` confirms: if the real session turns
 * out to belong to someone else, the store re-keys (`store.ts`).
 */
export function readStoredUserId(
  supabaseUrl: string | undefined,
  storage: Pick<KeyStore, 'getItem'> | undefined = browserStore('localStorage'),
): string | null {
  try {
    if (!storage?.getItem || !supabaseUrl) return null
    const ref = new URL(supabaseUrl).hostname.split('.')[0]
    const raw = storage.getItem(`sb-${ref}-auth-token`)
    if (!raw) return null
    const id = (JSON.parse(raw) as { user?: { id?: unknown } } | null)?.user?.id
    return typeof id === 'string' && id ? id : null
  } catch {
    return null
  }
}
